<template>
  <ComplianceReviewModal
    v-if="trustedContactId"
    :show="open"
    :trusted-contact-id="trustedContactId"
    :current-hire-id="hireId"
    @close="close"
    @saved="handleSaved"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import queriesToRefetch from '@/components/Hire/queriesToRefetch'
import { graphql } from '@/gql'
import { publicApi } from '@/app-bootstrapping/SetUpApollo'
import ComplianceReviewModal from '@/components/compliances/ComplianceReviewModal.vue'
import type { ComplianceModalProps } from '@/components/Hire/HirePageCompliance/modals/complianceModals'

const HireTrustedContactQuery = graphql(`
  query VerifiedWorkerComplianceModalHireTrustedContact($hireId: ID!, $companyId: ID!) {
    hire(id: $hireId) {
      id
      worker {
        id
      }
    }
  }
`)

const TrustedContactLookupQuery = graphql(`
  query VerifiedWorkerComplianceModalTrustedContactLookup(
    $accounts: [ID!]
    $workers: [ID!]
  ) {
    trustedContacts(accounts: $accounts, workers: $workers, first: 1) {
      data {
        id
      }
    }
  }
`)

const props = withDefaults(defineProps<ComplianceModalProps>(), {
  open: false,
  hideFooter: false,
})

const emit = defineEmits<{
  'update:open': [value: boolean]
}>()

const { result: hireResult } = useQuery(
  HireTrustedContactQuery,
  () => ({ hireId: props.hireId, companyId: currentAccount.globalId }),
  () => ({
    enabled: props.open,
    clientId: 'publicApi',
  }),
)

const workerId = computed(() => hireResult.value?.hire?.worker?.id ?? null)

const { result: tcResult } = useQuery(
  TrustedContactLookupQuery,
  () => ({ accounts: [currentAccount.globalId], workers: workerId.value ? [workerId.value] : [] }),
  () => ({
    enabled: props.open && workerId.value !== null,
    clientId: 'publicApi',
  }),
)

const trustedContactId = computed(() => tcResult.value?.trustedContacts?.data[0]?.id ?? null)

function close() {
  emit('update:open', false)
}

async function handleSaved() {
  await publicApi.refetchQueries({ include: queriesToRefetch })
}
</script>
